/**
 * Modern Spinner Component
 * A professional, accessible loading spinner with smooth animations and multiple variants.
 *
 * Features:
 * - Modern dash-offset animation with smooth easing
 * - Gradient color support
 * - Multiple animation variants (spin, pulse, chase)
 * - Fullscreen mode with branded backdrop
 * - Progress indicator variant
 * - Full accessibility support (WCAG 2.1 AA)
 * - Respects prefers-reduced-motion
 *
 * Usage:
 * - Inline: <Spinner size="sm" />
 * - With text: <Spinner text="Loading..." />
 * - Overlay: <Spinner mode="overlay" text="Saving..." />
 * - Fullscreen: <Spinner mode="fullscreen" text="Loading application..." />
 * - Modern variant: <Spinner variant="modern" />
 * - Progress: <Spinner variant="progress" progress={45} />
 */

import React from 'react';
import { theme } from '@/theme';

export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type SpinnerMode = 'inline' | 'centered' | 'overlay' | 'fullscreen';
export type SpinnerVariant = 'classic' | 'modern' | 'dots' | 'progress';

export interface SpinnerProps {
  /** Size of the spinner */
  size?: SpinnerSize;
  /** Display mode */
  mode?: SpinnerMode;
  /** Animation variant */
  variant?: SpinnerVariant;
  /** Optional loading text */
  text?: string;
  /** Custom color (defaults to brand accent) */
  color?: string;
  /** Secondary track color (defaults to light border) */
  trackColor?: string;
  /** Whether to show the spinner (useful for conditional rendering) */
  show?: boolean;
  /** Progress value (0-100) for progress variant */
  progress?: number;
  /** Additional class name */
  className?: string;
  /** Test ID for testing */
  testId?: string;
}

// Size configurations
const SIZES: Record<SpinnerSize, { spinner: number; stroke: number; fontSize: string }> = {
  xs: { spinner: 14, stroke: 2, fontSize: theme.typography.fontSizeXs },
  sm: { spinner: 18, stroke: 2, fontSize: theme.typography.fontSizeSm },
  md: { spinner: 24, stroke: 2.5, fontSize: theme.typography.fontSizeBase },
  lg: { spinner: 32, stroke: 3, fontSize: theme.typography.fontSizeMd },
  xl: { spinner: 48, stroke: 3.5, fontSize: theme.typography.fontSizeLg },
};

// Modern keyframes for enhanced animations
const SPINNER_KEYFRAMES = `
  @keyframes motr-spinner-rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes motr-spinner-dash {
    0% {
      stroke-dasharray: 1, 150;
      stroke-dashoffset: 0;
    }
    50% {
      stroke-dasharray: 90, 150;
      stroke-dashoffset: -35;
    }
    100% {
      stroke-dasharray: 90, 150;
      stroke-dashoffset: -124;
    }
  }

  @keyframes motr-spinner-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(0.95); }
  }

  @keyframes motr-dots-bounce {
    0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
    40% { transform: scale(1); opacity: 1; }
  }

  @keyframes motr-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes motr-text-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }

  @media (prefers-reduced-motion: reduce) {
    .motr-spinner-svg,
    .motr-spinner-modern,
    .motr-spinner-dots {
      animation: none !important;
    }
    .motr-spinner-svg circle,
    .motr-spinner-modern circle {
      animation: none !important;
    }
    .motr-spinner-text {
      animation: none !important;
    }
  }
`;

// Inject keyframes once
let keyframesInjected = false;
const injectKeyframes = () => {
  if (keyframesInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.id = 'motr-spinner-styles';
  style.textContent = SPINNER_KEYFRAMES;
  document.head.appendChild(style);
  keyframesInjected = true;
};

/**
 * Classic spinner - simple rotating arc
 */
const ClassicSpinner: React.FC<{
  size: SpinnerSize;
  color: string;
  trackColor: string;
}> = ({ size, color, trackColor }) => {
  const config = SIZES[size];
  const radius = (config.spinner - config.stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <svg
      className="motr-spinner-svg"
      width={config.spinner}
      height={config.spinner}
      viewBox={`0 0 ${config.spinner} ${config.spinner}`}
      fill="none"
      aria-hidden="true"
      style={{
        animation: 'motr-spinner-rotate 0.8s linear infinite',
        flexShrink: 0,
      }}
    >
      <circle
        cx={config.spinner / 2}
        cy={config.spinner / 2}
        r={radius}
        stroke={trackColor}
        strokeWidth={config.stroke}
        fill="none"
      />
      <circle
        cx={config.spinner / 2}
        cy={config.spinner / 2}
        r={radius}
        stroke={color}
        strokeWidth={config.stroke}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={`${circumference * 0.25} ${circumference * 0.75}`}
      />
    </svg>
  );
};

/**
 * Modern spinner - smooth dash animation with gradient-like effect
 */
const ModernSpinner: React.FC<{
  size: SpinnerSize;
  color: string;
  trackColor: string;
}> = ({ size, color, trackColor }) => {
  const config = SIZES[size];
  const radius = (config.spinner - config.stroke) / 2;
  const uniqueId = React.useId();

  return (
    <svg
      className="motr-spinner-modern"
      width={config.spinner}
      height={config.spinner}
      viewBox={`0 0 ${config.spinner} ${config.spinner}`}
      fill="none"
      aria-hidden="true"
      style={{
        animation: 'motr-spinner-rotate 2s linear infinite',
        flexShrink: 0,
      }}
    >
      <defs>
        {/* Gradient for modern look */}
        <linearGradient id={`spinner-gradient-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={color} stopOpacity="0.1" />
          <stop offset="50%" stopColor={color} stopOpacity="0.8" />
          <stop offset="100%" stopColor={color} stopOpacity="1" />
        </linearGradient>
      </defs>
      {/* Background track */}
      <circle
        cx={config.spinner / 2}
        cy={config.spinner / 2}
        r={radius}
        stroke={trackColor}
        strokeWidth={config.stroke}
        fill="none"
        opacity={0.3}
      />
      {/* Animated arc with dash animation */}
      <circle
        cx={config.spinner / 2}
        cy={config.spinner / 2}
        r={radius}
        stroke={`url(#spinner-gradient-${uniqueId})`}
        strokeWidth={config.stroke}
        fill="none"
        strokeLinecap="round"
        style={{
          transformOrigin: 'center',
          animation: 'motr-spinner-dash 1.5s ease-in-out infinite',
        }}
      />
    </svg>
  );
};

/**
 * Dots spinner - three bouncing dots
 */
const DotsSpinner: React.FC<{
  size: SpinnerSize;
  color: string;
}> = ({ size, color }) => {
  const config = SIZES[size];
  const dotSize = Math.max(config.spinner / 4, 4);
  const gap = dotSize * 0.6;

  return (
    <div
      className="motr-spinner-dots"
      aria-hidden="true"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: `${gap}px`,
        height: config.spinner,
      }}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            width: dotSize,
            height: dotSize,
            borderRadius: '50%',
            backgroundColor: color,
            animation: `motr-dots-bounce 1.4s ease-in-out infinite`,
            animationDelay: `${i * 0.16}s`,
          }}
        />
      ))}
    </div>
  );
};

/**
 * Progress spinner - circular progress indicator
 */
const ProgressSpinner: React.FC<{
  size: SpinnerSize;
  color: string;
  trackColor: string;
  progress: number;
}> = ({ size, color, trackColor, progress }) => {
  const config = SIZES[size];
  const radius = (config.spinner - config.stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const normalizedProgress = Math.min(100, Math.max(0, progress));
  const strokeDashoffset = circumference - (normalizedProgress / 100) * circumference;

  return (
    <svg
      className="motr-spinner-progress"
      width={config.spinner}
      height={config.spinner}
      viewBox={`0 0 ${config.spinner} ${config.spinner}`}
      fill="none"
      aria-hidden="true"
      style={{
        transform: 'rotate(-90deg)',
        flexShrink: 0,
      }}
    >
      {/* Background track */}
      <circle
        cx={config.spinner / 2}
        cy={config.spinner / 2}
        r={radius}
        stroke={trackColor}
        strokeWidth={config.stroke}
        fill="none"
      />
      {/* Progress arc */}
      <circle
        cx={config.spinner / 2}
        cy={config.spinner / 2}
        r={radius}
        stroke={color}
        strokeWidth={config.stroke}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        style={{
          transition: 'stroke-dashoffset 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />
    </svg>
  );
};

/**
 * Main Spinner Component
 * Default variant is configured in theme.spinner.defaultVariant
 */
export const Spinner: React.FC<SpinnerProps> = ({
  size = theme.spinner.defaultSize,
  mode = 'inline',
  variant = theme.spinner.defaultVariant,
  text,
  color = theme.spinner.defaultColor,
  trackColor = theme.colors.borderLight,
  show = true,
  progress = 0,
  className = '',
  testId = 'spinner',
}) => {
  React.useEffect(() => {
    injectKeyframes();
  }, []);

  if (!show) return null;

  const config = SIZES[size];

  // Render the appropriate spinner variant
  const renderSpinner = (spinnerSize: SpinnerSize) => {
    switch (variant) {
      case 'classic':
        return <ClassicSpinner size={spinnerSize} color={color} trackColor={trackColor} />;
      case 'dots':
        return <DotsSpinner size={spinnerSize} color={color} />;
      case 'progress':
        return <ProgressSpinner size={spinnerSize} color={color} trackColor={trackColor} progress={progress} />;
      case 'modern':
      default:
        return <ModernSpinner size={spinnerSize} color={color} trackColor={trackColor} />;
    }
  };

  // Inline mode - just the spinner
  const spinnerContent = (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid={testId}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: text ? theme.spacing.sm : 0,
      }}
    >
      {renderSpinner(size)}
      {text && (
        <span
          className="motr-spinner-text"
          style={{
            fontSize: config.fontSize,
            color: mode === 'overlay' || mode === 'fullscreen'
              ? theme.colors.textPrimary
              : theme.colors.textSecondary,
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          {text}
        </span>
      )}
      {/* Screen reader text */}
      <span
        className="sr-only"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          border: 0,
        }}
      >
        {text || 'Loading...'}
      </span>
    </div>
  );

  // Mode-specific wrappers
  switch (mode) {
    case 'inline':
      return spinnerContent;

    case 'centered':
      return (
        <div
          className={className}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing.xl,
            width: '100%',
            minHeight: '120px',
            animation: 'motr-fade-in 0.2s ease-out',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing.md }}>
            {spinnerContent}
          </div>
        </div>
      );

    case 'overlay':
      return (
        <div
          className={className}
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(4px)',
            borderRadius: 'inherit',
            zIndex: 10,
            animation: 'motr-fade-in 0.15s ease-out',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing.md }}>
            {spinnerContent}
          </div>
        </div>
      );

    case 'fullscreen':
      return (
        <div
          className={className}
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.colors.backgroundCard,
            backdropFilter: 'blur(8px)',
            zIndex: 9999,
            animation: 'motr-fade-in 0.2s ease-out',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: theme.spacing.lg,
              padding: theme.spacing.xl,
            }}
          >
            {/* Larger spinner for fullscreen */}
            <div
              style={{
                animation: 'motr-spinner-pulse 2s ease-in-out infinite',
              }}
            >
              {renderSpinner('xl')}
            </div>
            {text && (
              <span
                className="motr-spinner-text"
                style={{
                  fontSize: theme.typography.fontSizeLg,
                  color: theme.colors.textPrimary,
                  fontWeight: theme.typography.fontWeightMedium,
                  animation: 'motr-text-pulse 2s ease-in-out infinite',
                }}
              >
                {text}
              </span>
            )}
            {/* Screen reader text */}
            <span
              className="sr-only"
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: 'hidden',
                clip: 'rect(0,0,0,0)',
                border: 0,
              }}
            >
              {text || 'Loading...'}
            </span>
          </div>
        </div>
      );

    default:
      return spinnerContent;
  }
};

/**
 * Button Spinner - A smaller spinner designed for use inside buttons
 * Uses modern dash animation for a polished look
 */
export const ButtonSpinner: React.FC<{
  color?: string;
  className?: string;
}> = ({ color = 'currentColor', className = '' }) => {
  const uniqueId = React.useId();
  // Use 'currentColor' for gradient, custom color for direct stroke
  const useGradient = color === 'currentColor';

  React.useEffect(() => {
    injectKeyframes();
  }, []);

  return (
    <svg
      className={`motr-spinner-modern ${className}`}
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{
        animation: 'motr-spinner-rotate 1.5s linear infinite',
        flexShrink: 0,
        color: color !== 'currentColor' ? color : undefined,
      }}
    >
      {useGradient && (
        <defs>
          <linearGradient id={`btn-spinner-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.1" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="1" />
          </linearGradient>
        </defs>
      )}
      <circle
        cx={8}
        cy={8}
        r={6}
        stroke="currentColor"
        strokeOpacity={0.2}
        strokeWidth={2}
        fill="none"
      />
      <circle
        cx={8}
        cy={8}
        r={6}
        stroke={useGradient ? `url(#btn-spinner-${uniqueId})` : 'currentColor'}
        strokeWidth={2}
        fill="none"
        strokeLinecap="round"
        style={{
          animation: 'motr-spinner-dash 1.5s ease-in-out infinite',
        }}
      />
    </svg>
  );
};

/**
 * Loading Container - Wraps content and shows spinner when loading
 */
export interface LoadingContainerProps {
  loading: boolean;
  children: React.ReactNode;
  text?: string;
  size?: SpinnerSize;
  variant?: SpinnerVariant;
  mode?: 'overlay' | 'replace';
  minHeight?: string | number;
  className?: string;
}

export const LoadingContainer: React.FC<LoadingContainerProps> = ({
  loading,
  children,
  text,
  size = theme.spinner.defaultSize,
  variant = theme.spinner.defaultVariant,
  mode = 'overlay',
  minHeight = '100px',
  className = '',
}) => {
  if (mode === 'replace' && loading) {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight,
        }}
      >
        <Spinner size={size} text={text} mode="centered" variant={variant} />
      </div>
    );
  }

  return (
    <div className={className} style={{ position: 'relative', minHeight: loading ? minHeight : undefined }}>
      {children}
      {loading && <Spinner mode="overlay" size={size} text={text} variant={variant} />}
    </div>
  );
};

// Default export for convenience
export default Spinner;
